//Libraries
import { FC, useEffect, useState } from "react";
import { TablePagination, Alert } from "@mui/material";

//Providers
import { useAiSearch, useDiSearch,  useOrgSearch } from "src/services";

//Typing
import { IAISearch, IDISearch, IOrgSearch, ISearchOptions } from "src/services/org";

//Processing
import { formatAddress } from "src/generic/component/address";

//Components
import { ItemCard, IconButton, ErrorNotice, Loader, ItemRow, SubFieldLabel } from "src/generic/component";
import CheckIcon from "@mui/icons-material/Check";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";

interface IOrgSearchProps {
  params: ISearchOptions;
  onSelect: (select: IOrgSearch) => void;
}

export const OrgSearch: FC<IOrgSearchProps> = ({ params, onSelect }) => {
  const { orgs, isLoading, error } = useOrgSearch(params);
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(5);

  if (isLoading) {
    return (
      <div style={{ marginBottom: "20px" }}>
        <Loader />
      </div>
    );
  } else if (error) {
    return <ErrorNotice errorMessage={error}></ErrorNotice>;
  } else if (orgs) {
    const start = (page - 1) * perPage;
    const end = page * perPage;
    return (
      <>
        {orgs.length > 0 ? (
          <div>
            {orgs.slice(start, end).map((match, i) => {
              return (
                <div key={`match-container-${i}`}>
                  <ItemRow>
                    <ItemCard>
                      <div style={{ marginRight: "8px" }}>
                        <IconButton
                          onClick={() => {
                            onSelect(match);
                          }}
                        >
                          <CheckIcon />
                        </IconButton>
                      </div>
                      <div>
                        <strong>
                          {match.name} (OrgId: {match.orgId})
                        </strong>
                        {formatAddress(match)}
                        {match && match.ais && match.ais.length > 0 && (
                          <div>
                            <SubFieldLabel>
                              Attached AIs:&nbsp;
                              {match.ais.map((ai) => {
                                return (
                                  <span>
                                    {ai.aiCode}{" "}
                                    {ai.aiStatusCd === 1 ? (
                                      <RadioButtonCheckedIcon sx={{ color: "red", fontSize: 12 }} />
                                    ) : (
                                      <RadioButtonCheckedIcon sx={{ color: "green", fontSize: 12 }} />
                                    )}
                                  </span>
                                );
                              })}
                            </SubFieldLabel>
                          </div>
                        )}
                        {match && match.di && match.di.diCode &&(
                          <div>
                            <SubFieldLabel>
                              Attached DI:&nbsp;
                                  <span>
                                    {match.di.diCode}
                                  </span>
                            </SubFieldLabel>
                          </div>
                        )}
                      </div>
                    </ItemCard>
                  </ItemRow>
                </div>
              );
            })}
            <TablePagination
              component="div"
              sx={{
                ".MuiToolbar-root": {
                  paddingLeft: 0,
                },
                ".MuiTablePagination-spacer": {
                  flex: "none",
                },
              }}
              rowsPerPageOptions={[5, 10, 15]}
              count={orgs.length}
              rowsPerPage={perPage}
              page={page - 1}
              onPageChange={(e, num) => {
                setPage(num + 1);
              }}
              onRowsPerPageChange={(e) => {
                const val = parseInt(e.target.value);
                setPerPage(isNaN(val) ? 0 : val);
                setPage(1);
              }}
            />
          </div>
        ) : (
          <Alert style={{ marginBottom: "20px" }} severity="info">
            No organizations found
          </Alert>
        )}
      </>
    );
  } else {
    return (
      <Alert style={{ marginBottom: "20px" }} severity="info">
        No criteria for searching specified...
      </Alert>
    );
  }
};

interface IAiSearchDisplay {
  onSelect: (select: IAISearch) => void;
  matches: IAISearch[];
}

interface IAiSearchProps {
  params: ISearchOptions;
  view?: IAiSearchDisplay;
  onSelect: (select: IAISearch) => void;
}

const DefaultAiView: FC<IAiSearchDisplay> = ({ onSelect, matches }) => {
  return (
    <div>
      {matches.map((match) => {
        return (
          <div>
            <ItemRow>
              <ItemCard>
                <div style={{ marginRight: "8px" }}>
                  <IconButton
                    onClick={() => {
                      onSelect(match);
                    }}
                  >
                    <CheckIcon />
                  </IconButton>
                </div>
                <div>
                  <strong>
                    {match.aiStatusCd === 1 ? (
                      <RadioButtonCheckedIcon sx={{ color: "red", fontSize: 12 }} />
                    ) : (
                      <RadioButtonCheckedIcon sx={{ color: "green", fontSize: 12 }} />
                    )}{" "}
                    {match.name}
                  </strong>
                  <div>
                    <strong>
                      AI Code: {match.aiCode} &nbsp; AI Level: {match.aiLevelCd}
                    </strong>
                  </div>
                  {formatAddress(match)}
                  <div></div>
                </div>
              </ItemCard>
            </ItemRow>
          </div>
        );
      })}
    </div>
  );
};

export const AiSearch: FC<IAiSearchProps> = ({ params, onSelect }) => {
  const { ais, isLoading, error } = useAiSearch(params);

  useEffect(() => {
    setPage(1);
  }, [params]);

  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(5);

  if (isLoading) {
    return <Loader />;
  } else if (error) {
    return <ErrorNotice errorMessage={error}></ErrorNotice>;
  } else if (ais !== undefined) {
    const start = (page - 1) * perPage;
    const end = page * perPage;
    return (
      <>
        {ais.length > 0 ? (
          <div>
            <DefaultAiView matches={ais.slice(start, end)} onSelect={onSelect} />
            <TablePagination
              component="div"
              sx={{
                ".MuiToolbar-root": {
                  paddingLeft: 0,
                },
                ".MuiTablePagination-spacer": {
                  flex: "none",
                },
              }}
              rowsPerPageOptions={[5, 10, 15]}
              count={ais.length}
              rowsPerPage={perPage}
              page={page - 1}
              onPageChange={(e, num) => {
                setPage(num + 1);
              }}
              onRowsPerPageChange={(e) => {
                const val = parseInt(e.target.value);
                setPerPage(isNaN(val) ? 0 : val);
                setPage(1);
              }}
            />
          </div>
        ) : (
          <Alert severity="warning">No organizations found</Alert>
        )}
      </>
    );
  } else {
    return <Alert severity="info">Choose criteria to begin searching...</Alert>;
  }
};

interface IDiSearchDisplay {
  onSelect: (select: IDISearch) => void;
  matches: IDISearch[];
}

interface IDiSearchProps {
  params: ISearchOptions;
  view?: IDiSearchDisplay;
  onSelect: (select: IDISearch) => void;
}

const DefaultDiView: FC<IDiSearchDisplay> = ({ onSelect, matches }) => {
  return (
    <div>
      {matches.map((match) => {
        return (
          <div>
            <ItemRow>
              <ItemCard>
                <div style={{ marginRight: "8px" }}>
                  <IconButton
                    onClick={() => {
                      onSelect(match);
                    }}
                  >
                    <CheckIcon />
                  </IconButton>
                </div>
                <div>
                  <strong>
                    {match.name} (DI Code: {match.diCode})
                  </strong>
                  {formatAddress(match)}
                  <div></div>
                </div>
              </ItemCard>
            </ItemRow>
          </div>
        );
      })}
    </div>
  );
};

export const DiSearch: FC<IDiSearchProps> = ({ params, onSelect }) => {
  const { di, isLoading, error } = useDiSearch(params);

  useEffect(() => {
    setPage(1);
  }, [params]);

  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(5);

  if (isLoading) {
    return <Loader />;
  } else if (error) {
    return <ErrorNotice errorMessage={error}></ErrorNotice>;
  } else if (di !== undefined) {
    const start = (page - 1) * perPage;
    const end = page * perPage;
    return (
      <>
        {di ? (
          <div>
            <DefaultDiView matches={di.slice(start, end)} onSelect={onSelect} />
            <TablePagination
              component="div"
              sx={{
                ".MuiToolbar-root": {
                  paddingLeft: 0,
                },
                ".MuiTablePagination-spacer": {
                  flex: "none",
                },
              }}
              rowsPerPageOptions={[5, 10, 15]}
              count={di.length}
              rowsPerPage={perPage}
              page={page - 1}
              onPageChange={(e, num) => {
                setPage(num + 1);
              }}
              onRowsPerPageChange={(e) => {
                const val = parseInt(e.target.value);
                setPerPage(isNaN(val) ? 0 : val);
                setPage(1);
              }}
            />
          </div>
        ) : (
          <Alert severity="warning">No organizations found</Alert>
        )}
      </>
    );
  } else {
    return <Alert severity="info">Choose criteria to begin searching...</Alert>;
  }
};